import { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send, Loader2, MessageCircle, Trash2, History } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STORAGE_SESSION_KEY = "fc_assistant_session_id";
const WELCOME = {
  role: "assistant",
  content:
    "¡Hola! Soy tu asistente de FarmaClass. Dime qué quieres encontrar o hacer y te indico la ruta exacta. " +
    "Por ejemplo: \"¿Cómo descargo el PDF de incentivos?\", \"¿Dónde veo el stock muerto de Sandoz?\", \"¿Cómo configuro el baseline?\".",
  id: "welcome",
};

export default function AssistantChat() {
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_SESSION_KEY) || null);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && sessionId) {
      api.get(`/assistant/history/${sessionId}`)
        .then((r) => {
          const msgs = r.data?.messages || [];
          if (msgs.length > 0) setMessages(msgs);
        })
        .catch(() => {});
    }
  }, [open, sessionId]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const userMsg = { role: "user", content: text, id: `u-${Date.now()}`, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/assistant/chat", { message: text, session_id: sessionId });
      const ai = r.data?.message;
      const newSid = r.data?.session_id;
      if (newSid && newSid !== sessionId) {
        setSessionId(newSid);
        localStorage.setItem(STORAGE_SESSION_KEY, newSid);
      }
      if (ai) setMessages((m) => [...m, ai]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pude contactar con el asistente");
      setMessages((m) => [...m, { role: "assistant", content: "Lo siento, ha ocurrido un error. Inténtalo de nuevo.", id: `e-${Date.now()}` }]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const newSession = () => {
    setSessionId(null);
    localStorage.removeItem(STORAGE_SESSION_KEY);
    setMessages([WELCOME]);
    setShowHistory(false);
  };

  const loadSessions = async () => {
    setShowHistory(true);
    try {
      const r = await api.get("/assistant/sessions");
      setSessions(r.data?.sessions || []);
    } catch {
      setSessions([]);
    }
  };

  const switchSession = async (sid) => {
    setSessionId(sid);
    localStorage.setItem(STORAGE_SESSION_KEY, sid);
    setShowHistory(false);
    try {
      const r = await api.get(`/assistant/history/${sid}`);
      setMessages(r.data?.messages || [WELCOME]);
    } catch {
      setMessages([WELCOME]);
    }
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("¿Borrar esta conversación?")) return;
    try {
      await api.delete(`/assistant/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.session_id !== sid));
      if (sid === sessionId) newSession();
    } catch {
      toast.error("No se pudo borrar");
    }
  };

  const quickQuestions = [
    "¿Cómo descargo el PDF de incentivos?",
    "¿Dónde veo el stock muerto?",
    "¿Cómo configuro el baseline?",
    "¿Cómo subo un nuevo estudio?",
  ];

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-fab"
          className="fixed bottom-20 right-6 z-[60] group flex items-center gap-2.5 bg-gradient-to-br from-violet-600 to-indigo-700 text-white pl-3 pr-4 py-2.5 rounded-full shadow-2xl hover:shadow-violet-500/50 hover:scale-105 transition-all"
          title="¡Resuelve tus dudas aquí!"
          aria-label="Abrir asistente IA"
        >
          <span className="relative flex">
            <span className="absolute inset-0 rounded-full bg-violet-400 opacity-60 animate-ping"></span>
            <span className="relative bg-white/20 backdrop-blur p-1.5 rounded-full">
              <Sparkles className="h-4 w-4 group-hover:rotate-12 transition-transform" />
            </span>
          </span>
          <div className="hidden sm:flex flex-col items-start leading-tight">
            <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-violet-200">Ayuda IA</span>
            <span className="text-xs font-extrabold">¡Resuelve tus dudas aquí!</span>
          </div>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-20 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[420px] h-[600px] max-h-[calc(100vh-7rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-violet-600 to-indigo-700 text-white px-4 py-3 flex items-center gap-2">
            <div className="bg-white/20 backdrop-blur p-1.5 rounded-lg">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm leading-tight">FarmaClass Assistant</div>
              <div className="text-[10px] uppercase tracking-wider opacity-80">Powered by Claude · respondo en español</div>
            </div>
            <button
              onClick={loadSessions}
              data-testid="assistant-history-btn"
              className="text-white/80 hover:text-white p-1.5 rounded hover:bg-white/10"
              title="Conversaciones anteriores"
              aria-label="Historial"
            >
              <History className="h-4 w-4" />
            </button>
            <button
              onClick={newSession}
              data-testid="assistant-new-btn"
              className="text-white/80 hover:text-white p-1.5 rounded hover:bg-white/10"
              title="Nueva conversación"
              aria-label="Nueva conversación"
            >
              <MessageCircle className="h-4 w-4" />
            </button>
            <button
              onClick={() => setOpen(false)}
              data-testid="assistant-close-btn"
              className="text-white/80 hover:text-white p-1.5 rounded hover:bg-white/10"
              aria-label="Cerrar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* History panel */}
          {showHistory && (
            <div className="absolute inset-0 top-[60px] bg-white z-10 overflow-y-auto p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500">Conversaciones</h3>
                <button onClick={() => setShowHistory(false)} className="text-xs text-violet-700 hover:underline">Volver</button>
              </div>
              {sessions.length === 0 ? (
                <div className="text-sm text-slate-500 italic py-8 text-center">No tienes conversaciones guardadas todavía.</div>
              ) : (
                <div className="space-y-1.5">
                  {sessions.map((s) => (
                    <button
                      key={s.session_id}
                      onClick={() => switchSession(s.session_id)}
                      data-testid={`assistant-session-${s.session_id}`}
                      className={`w-full text-left p-2.5 rounded-lg border transition group ${
                        s.session_id === sessionId
                          ? "bg-violet-50 border-violet-300"
                          : "bg-white border-slate-200 hover:border-violet-300 hover:bg-violet-50/50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs text-slate-800 line-clamp-2">{s.preview || "(sin texto)"}</div>
                          <div className="text-[10px] text-slate-500 mt-1 font-mono-num">
                            {s.message_count} mensajes · {s.last_at ? new Date(s.last_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : ""}
                          </div>
                        </div>
                        <button onClick={(e) => deleteSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700 p-1" title="Borrar" aria-label="Borrar conversación">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} data-testid="assistant-messages" className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${
                    m.role === "user"
                      ? "bg-violet-600 text-white rounded-br-sm"
                      : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
                  }`}
                  data-testid={`assistant-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5 inline-flex items-center gap-2 shadow-sm">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-600" />
                  <span className="text-xs text-slate-500">Pensando…</span>
                </div>
              </div>
            )}
          </div>

          {/* Quick questions (only show when no user messages yet) */}
          {messages.length <= 1 && !sending && (
            <div className="px-3 pb-2 bg-slate-50/50">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Preguntas frecuentes</div>
              <div className="flex flex-wrap gap-1.5">
                {quickQuestions.map((q) => (
                  <button
                    key={q}
                    onClick={() => setInput(q)}
                    className="text-[11px] px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-violet-300 hover:bg-violet-50 transition"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-slate-200 p-3 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                data-testid="assistant-input"
                placeholder="Escribe tu pregunta…"
                rows={1}
                className="flex-1 resize-none text-sm rounded-xl border border-slate-300 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 px-3 py-2 max-h-24 outline-none"
                disabled={sending}
              />
              <button
                onClick={send}
                disabled={sending || !input.trim()}
                data-testid="assistant-send"
                className="p-2.5 rounded-xl bg-violet-600 text-white hover:bg-violet-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition"
                aria-label="Enviar"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <div className="text-[10px] text-slate-400 mt-1.5 px-1">Pulsa Enter para enviar · Shift+Enter para nueva línea</div>
          </div>
        </div>
      )}
    </>
  );
}
